import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, ShieldCheck, Users, BookOpen, Award, Compass, HeartHandshake, Globe2, Sparkles, Star } from "lucide-react";
import { api, IMG, HERO_VIDEO_WEBM, HERO_VIDEO_MP4 } from "@/lib/api";
import CourseCard from "@/components/courses/CourseCard";

const HERO_IMG = IMG.hero;
const MOUNTAIN_IMG = "https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";
const COMMUNITY_IMG = IMG.community;
const PUPS_IMG = IMG.puppies;
const FAMILY_IMG = IMG.family;
const SANCTUARY_IMG = IMG.packSnow;
const TRAINER_IMG = IMG.grass;
const FOREST_IMG = "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";
const HOWL_IMG = IMG.howl;

const AUDIENCES = [
  { title: "Future Owners", desc: "Learn before you bring one home.", img: PUPS_IMG, icon: Compass, to: "/learn" },
  { title: "Current Owners", desc: "Build a stronger relationship through education.", img: FAMILY_IMG, icon: HeartHandshake, to: "/learn" },
  { title: "Responsible Breeders", desc: "Raise healthier puppies, ethically.", img: FOREST_IMG, icon: ShieldCheck, to: "/learn" },
  { title: "Rescue & Sanctuaries", desc: "Helping wolfdogs find lifelong success.", img: SANCTUARY_IMG, icon: Users, to: "/directory" },
  { title: "Conservation", desc: "Ethical stewardship and coexistence.", img: MOUNTAIN_IMG, icon: Globe2, to: "/about" },
  { title: "Veterinary & Pros", desc: "Field-tested resources for practitioners.", img: TRAINER_IMG, icon: BookOpen, to: "/directory" },
];

const JOURNEY = [
  { title: "Discover", copy: "Is a wolfdog right for me?" },
  { title: "Prepare", copy: "Learn. Plan. Get ready." },
  { title: "Grow", copy: "Raise with knowledge. Build trust." },
  { title: "Thrive", copy: "Navigate real life together." },
  { title: "Together", copy: "A lifetime of commitment." },
];

export default function Home() {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/courses").then(r => setCourses(r.data)).catch(() => {});
  }, []);

  const featured = courses.filter(c => c.featured);
  const latest = courses.slice(0, 6);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] w-full overflow-hidden">
        <video
          poster={HERO_IMG}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="hero-video"
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src={HERO_VIDEO_WEBM} type="video/webm" />
          <source src={HERO_VIDEO_MP4} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/30 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-transparent to-transparent" />

        <div className="relative container-x pt-32 md:pt-40 pb-24">
          <div className="max-w-3xl">
            <div className="eyebrow reveal text-foreground/90">The Global Institute for Wolfdog Ownership</div>
            <h1 className="font-display text-6xl md:text-8xl lg:text-[8.5rem] leading-[0.9] tracking-tight mt-4 reveal reveal-1 text-balance">
              Welcome to <span className="italic text-primary">Wolfdog</span> Village.
            </h1>
            <p className="mt-8 text-base md:text-xl text-foreground/85 max-w-2xl reveal reveal-2 leading-relaxed text-pretty">
              The world's leading educational resource for responsible wolfdog ownership,
              breeding, conservation, and lifelong learning.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3 reveal reveal-3">
              <Link to="/learn" data-testid="hero-cta-start-learning" className="btn-primary">
                Start Learning <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/certification" data-testid="hero-cta-become-member" className="btn-outline">
                <Play className="h-4 w-4" /> Become a Member
              </Link>
            </div>

            <div className="mt-14 flex items-center gap-6 reveal reveal-4">
              <div>
                <div className="font-display text-3xl">5,000+</div>
                <div className="eyebrow mt-1">Certified Learners</div>
              </div>
              <div className="h-10 w-px bg-foreground/20" />
              <div>
                <div className="font-display text-3xl">42</div>
                <div className="eyebrow mt-1">Faculty & Mentors</div>
              </div>
              <div className="h-10 w-px bg-foreground/20" />
              <div>
                <div className="font-display text-3xl">51</div>
                <div className="eyebrow mt-1">Legal Jurisdictions</div>
              </div>
            </div>
          </div>
        </div>

      </section>

      {/* JOURNEY SCRUBBER */}
      <section className="container-x -mt-12 md:-mt-16 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
          {JOURNEY.map((j, i) => (
            <div key={j.title} className="rounded-md border border-border bg-card px-4 py-5 shadow-sm reveal" style={{ animationDelay: `${i * 0.08}s` }}>
              <div className="eyebrow text-[10px]">Step {i + 1}</div>
              <div className="font-display text-2xl mt-1">{j.title}</div>
              <div className="text-xs text-muted-foreground mt-1">{j.copy}</div>
            </div>
          ))}
        </div>
      </section>

      {/* AUDIENCE / WHO WE SUPPORT */}
      <section className="container-x py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="eyebrow">Who we support</div>
            <h2 className="font-display text-4xl md:text-6xl mt-3 leading-[1.02] tracking-tight text-balance">
              Education for every stage of the <span className="italic text-primary">journey</span>.
            </h2>
          </div>
          <p className="text-muted-foreground max-w-md text-pretty">
            From first curiosity to lifetime mentorship, our curriculum is built with breeders, veterinarians, sanctuaries and cognitive scientists.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {AUDIENCES.map((a, i) => {
            const Icon = a.icon;
            return (
              <Link
                key={a.title}
                to={a.to}
                data-testid={`audience-card-${a.title.toLowerCase().replace(/\s+/g,'-')}`}
                className="group relative aspect-[4/5] overflow-hidden rounded-lg border border-border reveal"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <img src={a.img} alt={a.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
                <div className="absolute inset-0 p-6 flex flex-col justify-between text-white">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/30 backdrop-blur">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="font-display text-3xl leading-tight">{a.title}</div>
                    <div className="mt-2 max-w-xs text-sm text-white/85">{a.desc}</div>
                    <div className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em]">
                      Explore <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Editorial split — Mission */}
      <section className="relative overflow-hidden border-y border-border">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 py-24 md:py-32 items-center">
          <div className="lg:col-span-5">
            <div className="eyebrow">Our mission</div>
            <h2 className="font-display text-4xl md:text-6xl mt-3 leading-[1.02] tracking-tight text-balance">
              Educating people. Strengthening relationships. <span className="italic text-primary">Improving the future</span> of wolfdogs.
            </h2>
            <p className="mt-6 text-muted-foreground text-pretty">
              We're not a breeder. Not a marketplace. Not a forum. We are a modern educational institution — built with the rigor of a university and the heart of a conservation movement.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/about" className="btn-primary" data-testid="mission-cta-about">Read our story</Link>
              <Link to="/certification" className="btn-outline" data-testid="mission-cta-cert">See Certification</Link>
            </div>
          </div>
          <div className="lg:col-span-7 relative aspect-[16/10] rounded-lg overflow-hidden">
            <img src={MOUNTAIN_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/40 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white flex items-end justify-between">
              <div className="font-display text-2xl md:text-3xl italic leading-tight max-w-md text-balance">
                "We're not just raising a pet, we're raising a lifelong companion."
              </div>
              <div className="hidden md:block eyebrow text-white/90">— Village Member</div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED COURSES */}
      <section className="container-x py-24 md:py-32">
        <div className="flex items-end justify-between gap-6">
          <div>
            <div className="eyebrow">Learning Center</div>
            <h2 className="font-display text-4xl md:text-6xl mt-3 leading-[1.02] tracking-tight text-balance">
              Courses built with <span className="italic text-primary">the world's</span> best educators.
            </h2>
          </div>
          <Link to="/learn" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold hover:text-primary" data-testid="home-view-all-courses">
            View all courses <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.slice(0, 3).map((c) => <CourseCard key={c.id} c={c} />)}
        </div>
        {latest.length > 3 && (
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            {latest.slice(3, 6).map(c => <CourseCard key={c.id} c={c} />)}
          </div>
        )}
      </section>

      {/* Certification band */}
      <section className="relative overflow-hidden">
        <img src={HOWL_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-background/85" />
        <div className="relative container-x py-24 md:py-32 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="eyebrow">Certification</div>
            <h2 className="font-display text-4xl md:text-6xl mt-3 leading-[1.02] tracking-tight text-balance">
              Earn credentials that <span className="italic text-primary">breeders trust</span>.
            </h2>
            <p className="mt-6 text-muted-foreground max-w-lg text-pretty">
              From the Future Owner Certificate to the Responsible Breeder Program, our certifications are recognized across the wolfdog community and required by participating breeders.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/certification" className="btn-gold" data-testid="cert-band-cta">Explore Certification</Link>
              <Link to="/learn" className="btn-outline">See the Curriculum</Link>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
            {[
              { title: "Future Owner", tier: "Bronze", icon: Compass },
              { title: "Ownership 101", tier: "Bronze", icon: BookOpen },
              { title: "Ownership 201", tier: "Silver", icon: ShieldCheck },
              { title: "Ownership 301", tier: "Gold", icon: Award },
              { title: "Certified Breeder", tier: "Gold", icon: Star },
              { title: "Continuing Ed", tier: "Silver", icon: Sparkles },
            ].map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="rounded-lg border border-border bg-card p-5 flex items-start justify-between gap-3 hover:-translate-y-1 transition">
                  <div>
                    <div className="font-display text-xl">{b.title}</div>
                    <div className="eyebrow mt-1 text-[10px]">{b.tier}</div>
                  </div>
                  <div className="h-10 w-10 rounded-full grid place-items-center bg-accent/15 text-accent">
                    <Icon className="h-4 w-4" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonial marquee */}
      <section className="border-y border-border overflow-hidden">
        <div className="container-x py-14 md:py-20">
          <div className="max-w-3xl">
            <div className="eyebrow">From the community</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3 leading-tight tracking-tight text-balance">
              Real owners. Real breeders. <span className="italic text-primary">Real change.</span>
            </h2>
          </div>
        </div>
        <div className="pb-16 relative">
          <div className="flex gap-5 marquee-track" style={{ width: "200%" }}>
            {[...Array(2)].map((_, k) => (
              <div className="flex gap-5" key={k}>
                {[
                  { q: "The knowledge and community I found here changed everything.", a: "— Priya S., Ownership 101 Graduate" },
                  { q: "Requiring the Future Owner Certificate has completely changed the quality of my placements.", a: "— Whitepine Wolfdogs, Certified Breeder" },
                  { q: "This is the National Geographic of wolfdog education.", a: "— Dr. Mara Lee, DVM" },
                  { q: "The behavior curriculum is unlike anything else in this space.", a: "— Kenji T., Ownership 201" },
                  { q: "Our sanctuary uses Wolfdog Village as its adoption pre-requisite.", a: "— Silverpaw Sanctuary" },
                ].map((t, i) => (
                  <figure key={i} className="min-w-[380px] md:min-w-[520px] rounded-lg border border-border bg-card p-8">
                    <blockquote className="font-display text-2xl md:text-3xl leading-snug italic text-pretty">"{t.q}"</blockquote>
                    <figcaption className="mt-6 eyebrow">{t.a}</figcaption>
                  </figure>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community teaser */}
      <section className="container-x py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 relative aspect-[4/3] rounded-lg overflow-hidden">
            <img src={COMMUNITY_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="lg:col-span-6">
            <div className="eyebrow">Community</div>
            <h2 className="font-display text-4xl md:text-6xl mt-3 leading-[1.02] tracking-tight text-balance">
              A village of educators, <span className="italic text-primary">learners and mentors</span>.
            </h2>
            <p className="mt-6 text-muted-foreground max-w-lg text-pretty">
              Monthly live Q&As, member spotlights, breeder features and success stories from thousands of members across 51 jurisdictions.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/community" className="btn-primary" data-testid="home-cta-community">Enter the Community</Link>
              <Link to="/events" className="btn-outline">See Events</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
